// Relay transport for the web chat — analystbro-desktop/docs/004 §7 phase 5.
//
// The browser cannot speak ACP, so it posts a turn to prod and prod routes it
// to whichever host serves this user: their own AnalystBro Desktop, or cloud
// hermes. Same mechanism the Excel add-in already uses; this is the third
// surface on one contract, not a third system.
//
// `streamTurn` is an async generator yielding the SAME event shapes as
// `streamChat` in sseClient.js — { type: 'content' | 'tool' | 'ping' } — plus
// `'image'` (2026-09, desktop/web image-parity fix: a completed chart tool's
// PNG rides in the tool_call_update's own `content` array, same as ACP's
// session/update the desktop consumes; sseClient's hermes-direct path has no
// equivalent yet since hermes never produced inline chart images before this).
// HermesChat's consuming loop should not otherwise change. If swapping the
// transport had required changing how the chat RENDERS, the host contract
// would have been wrong.
import { API_BASE_URL } from '../../config';

const POLL_MS = 900;
/** A turn nothing ever claims has to stop, or the composer spins forever. */
const MAX_WAIT_MS = 10 * 60 * 1000;

/** Ported from analystbro-desktop's format.ts bareTool() -- same three ACP
 * runtime naming conventions can show up here too, since the relay just
 * carries whichever runtime the desktop is actually driving. */
function bareTool(name) {
  return (name || '')
    .replace(/^mcp__[a-z0-9_]+__/i, '')
    .replace(/^mcp\.[a-z0-9_]+\./i, '')
    .replace(/^(analystbro|chart)-/i, '');
}

/** Ported from analystbro-desktop's App.tsx drawnSymbol() -- chart_draw takes
 * no symbol argument, so which chart a shape landed on is only in its own
 * result text ("drew trend_line (id x) on BATS:JPM"). */
function drawnSymbol(content) {
  if (!Array.isArray(content)) return '';
  for (const block of content) {
    const t = block?.text ?? block?.content?.text;
    if (typeof t === 'string' && t.startsWith('drew ') && t.includes(' on ')) {
      return t.split(' on ').slice(1).join(' on ').trim();
    }
  }
  return '';
}

function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Which hosts can serve this user right now.
 *
 * Decided by prod, not here: three surfaces reimplementing entitlement is three
 * chances to offer a button that 403s.
 */
export async function getPresence() {
  const r = await fetch(`${API_BASE_URL}/api/relay/presence`, {
    headers: { ...authHeaders() },
  });
  if (!r.ok) throw new Error(`presence failed (${r.status})`);
  return r.json();
}

/**
 * Fallback history read for a thread that was answered via the relay
 * (AnalystBro Front docs/12) -- the cloud hermes session store never saw a
 * relay-routed turn, so `getSessionMessages` legitimately returns nothing for
 * one. Returns `null` (never throws) when there's nothing here, same as a
 * fresh/empty hermes session -- HermesChat's loader treats both identically.
 */
export async function getRelayThreadHistory(threadId) {
  const r = await fetch(`${API_BASE_URL}/api/relay/history/${encodeURIComponent(threadId)}`, {
    headers: { ...authHeaders() },
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`relay history failed (${r.status})`);
  return r.json();
}

/**
 * Map a relay history response into HermesChat's message shape. Simpler than
 * hermesChats.js's mapHermesMessages: each row already carries its OWN full
 * `meta` (tool trail + images) as saved by the desktop's own persistTurn --
 * no cross-referencing a separate tool-call/tool-result stream needed.
 */
/** The fields HtmlRenderFrame needs from an html_render block. Relative
 * `url`s (from history) are made absolute against the API. */
function pickVisual(b) {
  const url = typeof b.url === 'string' ? (b.url.startsWith('/') ? `${API_BASE_URL}${b.url}` : b.url) : null;
  return { attachmentId: b.attachmentId, title: b.title || 'Visualization', height: b.height, heights: b.heights, url };
}

export function mapRelayHistory(data) {
  if (!data?.messages?.length) return [];
  return data.messages.map((m) => {
    if (m.role === 'user') return { role: 'user', content: m.content || '' };
    const meta = m.meta || {};
    return {
      role: 'assistant',
      content: m.content || '',
      activity: Array.isArray(meta.tools)
        ? meta.tools.map((t, i) => ({ id: `relay-history-${i}`, tool: '', emoji: '', label: t.label || '', status: 'completed', durationMs: t.ms ?? null }))
        : [],
      visuals: Array.isArray(meta.attachments)
        ? meta.attachments.filter((a) => a?.type === 'html_render' && a.attachmentId).map(pickVisual)
        : [],
      cards: Array.isArray(meta.attachments)
        ? meta.attachments.filter((a) => a?.type === 'backtest_run' || a?.type === 'paper_account' || a?.type === 'alert' || a?.type === 'watchlist')
        : [],
      images: Array.isArray(meta.images)
        ? meta.images.map((src) => {
            const match = /^data:([^;]+);base64,(.*)$/.exec(src || '');
            return match ? { mimeType: match[1], data: match[2] } : null;
          }).filter(Boolean)
        : [],
    };
  });
}

async function submit(prompt, threadId, route) {
  const r = await fetch(`${API_BASE_URL}/api/relay/turns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    // Name ourselves so the desktop can show WHO it is working for. A label
    // only — the relay authorizes on the JWT, never on this.
    body: JSON.stringify({ prompt, thread_id: threadId, route, surface: 'Web' }),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `could not send (${r.status})`);
  }
  return (await r.json()).turn_id;
}

/**
 * Submit a turn and stream its events.
 *
 * Polls rather than streams: the relay is plain HTTP because prod is
 * gunicorn/WSGI with no Channels. Doc 05 §2a unified the schema, not the
 * transport, so moving to SSE later changes this function and nothing above it.
 */
export async function* streamTurn({ prompt, threadId, route = 'auto', signal }) {
  const turnId = await submit(prompt, threadId, route);
  const started = Date.now();
  let after = -1;
  let announced = false;

  for (;;) {
    if (signal?.aborted) return;
    if (Date.now() - started > MAX_WAIT_MS) {
      throw new Error('the agent did not respond in time — no host picked this up');
    }

    const r = await fetch(
      `${API_BASE_URL}/api/relay/turns/${turnId}/events?after=${after}`,
      { headers: { ...authHeaders() }, signal },
    );
    if (!r.ok) throw new Error(`events failed (${r.status})`);
    const d = await r.json();

    // Which host took it, once the relay knows. Yielded as a tool-shaped frame
    // so the existing renderer shows it without new plumbing — the Excel pane
    // shows the same thing as a badge (ChatView's ServedByBadge), and a user
    // spending their own subscription should be able to see that they are.
    if (d.served_by && !announced) {
      announced = true;
      yield {
        type: 'tool',
        id: 'served-by',
        tool: 'served_by',
        emoji: d.served_by.kind === 'desktop' ? '🖥' : '☁',
        label: d.served_by.kind === 'desktop'
          ? `answered on ${d.served_by.label || 'your machine'}`
          : 'answered in the cloud',
        status: 'done',
      };
    }

    for (const e of d.events ?? []) {
      // seq ordering, so a replayed event cannot duplicate text
      after = Math.max(after, e.seq);
      const p = e.payload ?? {};
      if (p.sessionUpdate === 'agent_message_chunk') {
        const text = p.content?.text ?? '';
        if (text) yield { type: 'content', text };
      } else if (p.sessionUpdate === 'tool_call' || p.sessionUpdate === 'tool_call_update') {
        const bare = bareTool(p._meta?.claudeCode?.toolName || p.title || '');
        yield {
          type: 'tool',
          id: p.toolCallId || p.title || 'tool',
          tool: p._meta?.claudeCode?.toolName || p.title || 'tool',
          emoji: '',
          label: p.title || '',
          // Must be the literal string HermesChat's applyTool checks for --
          // sseClient's own hermes frames already send 'completed' straight
          // through (see sseClient.js), and applyTool computes durationMs
          // only on `status === 'completed'`. This used to translate to
          // 'done', which never matched, so every relay-routed tool chip
          // silently lost its `~4.5s` timing (desktop computes and shows it
          // fine because acp.rs's own event carries the real status string).
          status: p.status === 'completed' ? 'completed' : 'running',
        };
        // Desktop's collectImages() (App.tsx) scans this same `content` array
        // for an image block -- a completed chart_snap/chart_draw's PNG/JPEG
        // rides here, not in `agent_message_chunk`. The web previously only
        // ever read `p.content?.text` above, so this was silently dropped
        // even though the bytes arrive in the same event the desktop already
        // renders from.
        //
        // Verified live against a real relay event (2026-09): each array
        // entry is a WRAPPER -- `{type:"content", content:{type, data,
        // mimeType}}` -- not a flat `{type:"image", data, mimeType}` as
        // first assumed. The real payload is one level deeper, under
        // `.content`. Confirmed via a direct fetch of a completed
        // chart_snap turn's own /api/relay/turns/.../events response.
        //
        // `tool`/`sym` mirror desktop's App.tsx Part shape exactly, so
        // HermesChat's visibleImages() can apply the SAME one-chart-per-symbol
        // collapse desktop's visibleParts() does, instead of showing every
        // intermediate chart_snap/chart_draw frame as its own image.
        if (Array.isArray(p.content)) {
          const args = p.rawInput?.arguments ?? p.rawInput ?? {};
          const sym = drawnSymbol(p.content) || args.symbol || '';
          for (const block of p.content) {
            const inner = block?.content ?? block;
            if (inner?.type === 'image' && inner.data) {
              yield { type: 'image', data: inner.data, mimeType: inner.mimeType || 'image/png', tool: bare, sym };
            } else if (block?.type === 'html_render' && block.attachmentId) {
              // An inline visualization; the host uploaded its HTML and set
              // `url` (desktop docs/022 §8 phase 2).
              yield { type: 'visual', visual: pickVisual(block) };
            } else if (['backtest_run', 'paper_account', 'alert', 'watchlist'].includes(block?.type)) {
              // A Backtest / Paper account card (desktop docs/040 §9, 041 §5),
              // drawn by the shared UI (src/shared-ui).
              yield { type: 'card', card: block };
            }
          }
        }
      } else if (p.sessionUpdate === 'error') {
        throw new Error(p.message || 'the agent reported an error');
      }
    }

    if (d.status === 'done') return;
    if (d.status === 'error') {
      // Surfaced as an error rather than an empty answer — an absent reply
      // reads as "the agent had nothing to say", which is the failure mode
      // this whole surface keeps hitting.
      throw new Error('the agent failed to answer');
    }

    // Proof of life while nothing has arrived yet, so the stall detector in
    // HermesChat does not fire during a normal wait for a host.
    yield { type: 'ping' };
    await new Promise((res) => setTimeout(res, POLL_MS));
  }
}
